import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { CreditCard, Loader2, FileText, Shield, AlertCircle, Car, Download, Phone, Mail, Lock, Calendar, Wrench, Package } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { toPublicStorageUrl } from '../lib/url';
import { errorMessage, reportError } from '../lib/errors';
import { DEFAULT_DOCUMENT_ACCENT } from '../lib/brandAccent';

interface LineItem {
    description: string;
    item_type: string;
    quantity: number;
    unit_price: number;
    line_total: number;
}

interface InvoiceLanding {
    invoice_id: string;
    invoice_number: string;
    status: string;
    total_gross: number;
    vat_amount: number;
    amount_paid: number;
    balance_due: number;
    due_date: string | null;
    invoice_date: string | null;
    customer_name: string;
    vehicle_reg: string;
    vehicle_make: string;
    vehicle_model: string;
    vehicle_year: number;
    garage_name: string;
    garage_branding: Record<string, any>;
    garage_phone: string | null;
    garage_email: string | null;
    // Absent from the token-gated payload by design (audit finding F-08):
    // neither is needed to display or pay an invoice, and org_id is what made
    // the diagnostic-photos bucket enumerable.
    pdf_storage_path?: string | null;
    pdf_url?: string;
    org_id?: string;
    line_items: LineItem[];
}

export default function PaymentLanding() {
    // Reached only via /pay/t/:token. The old /pay/invoice/:invoiceId route was
    // retired on 2026-08-03 (audit finding F-08) — a raw invoice UUID was a
    // bearer credential with no expiry and no revocation, so anyone who ever saw
    // one kept access to that customer's invoice forever.
    const { token } = useParams<{ token?: string }>();
    // The invoice id is only known once the token resolves.
    const [resolvedInvoiceId, setResolvedInvoiceId] = useState<string | null>(null);
    const [invoice, setInvoice] = useState<InvoiceLanding | null>(null);
    const [loading, setLoading] = useState(true);
    const [paying, setPaying] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showItems, setShowItems] = useState(false);

    useEffect(() => {
        if (!token) {
            setError('No invoice reference found.');
            setLoading(false);
            return;
        }

        const fetchInvoice = async () => {
            try {
                const { data, error: rpcError } = await supabase.rpc(
                    'fn_get_public_invoice_by_token',
                    { p_token: token },
                );

                if (rpcError) throw rpcError;

                // An expired or revoked token comes back as { valid: false }
                // rather than throwing.
                if (!data || data.valid === false) {
                    setError('This payment link has expired. Please ask the garage to resend it.');
                    setLoading(false);
                    return;
                }

                setResolvedInvoiceId(data.invoice_id ?? null);

                // If invoice is already fully paid, redirect to success page
                if (data.status === 'paid') {
                    window.location.href = `/pay/success?token=${encodeURIComponent(token)}`;
                    return;
                }

                // The invoice PDF link has to be minted server-side.
                //
                // This used to call storage.createSignedUrl() from the browser
                // against the private `invoices-issued` bucket. As `anon` that
                // is denied by storage RLS, so the customer's download silently
                // never worked. The get-public-invoice edge function holds the
                // service role and returns a short-lived signed URL — and never
                // the storage path itself.
                try {
                    const { data: pdfRes, error: pdfResError } = await supabase.functions.invoke(
                        `get-public-invoice?token=${encodeURIComponent(token)}`,
                        { method: 'GET' },
                    );
                    if (pdfResError) reportError(pdfResError, 'payment-landing.get-public-invoice');
                    if (pdfRes?.data?.pdf_url) {
                        data.pdf_url = toPublicStorageUrl(pdfRes.data.pdf_url);
                    }
                } catch (pdfErr) {
                    // A missing PDF must not block payment.
                    console.warn('Could not resolve invoice PDF link:', pdfErr);
                }

                setInvoice(data);
            } catch (err) {
                console.error('Failed to load invoice:', err);
                setError('Unable to load invoice details. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        fetchInvoice();
    }, [token]);

    const handlePayNow = async () => {
        if (!resolvedInvoiceId || paying) return;
        setPaying(true);
        setError(null);

        try {
            const { data, error: fnError } = await supabase.functions.invoke('create-invoice-checkout', {
                // Pass the portal token: it is what proves this anonymous caller
                // is entitled to this invoice, including auto-issuing it if it is
                // still a draft (audit finding E-09).
                body: { invoice_id: resolvedInvoiceId, token },
            });

            if (fnError) throw fnError;

            if (data?.checkout_url) {
                window.location.href = data.checkout_url;
            } else {
                throw new Error('Could not create payment session');
            }
        } catch (err) {
            console.error('Failed to start checkout:', err);
            setError(errorMessage(err) || 'Failed to start payment. Please try again.');
            setPaying(false);
        }
    };

    const accentColor = invoice?.garage_branding?.invoice_accent_color || invoice?.garage_branding?.accent_color || DEFAULT_DOCUMENT_ACCENT;
    const logoUrl = invoice?.garage_branding?.logo_url;

    const formatDate = (dateStr: string | null) => {
        if (!dateStr) return null;
        return new Date(dateStr).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        });
    };

    // Separate line items by type
    const labourItems = invoice?.line_items?.filter(li => li.item_type === 'labour') || [];
    const partItems = invoice?.line_items?.filter(li => li.item_type === 'part' || li.item_type === 'parts') || [];
    const otherItems = invoice?.line_items?.filter(li => !['labour', 'part', 'parts'].includes(li.item_type)) || [];
    const allItems = [...labourItems, ...partItems, ...otherItems];

    const hasPaidSomething = invoice && invoice.amount_paid > 0 && invoice.balance_due > 0;

    if (loading) {
        return (
            <div className="min-h-screen bg-panel flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="w-10 h-10 animate-spin text-success mx-auto mb-4" />
                    <p className="text-fg-3 text-sm">Loading invoice...</p>
                </div>
            </div>
        );
    }

    if (error && !invoice) {
        return (
            <div className="min-h-screen bg-panel flex items-center justify-center p-6">
                <div className="max-w-md w-full bg-tint/5 backdrop-blur-xl border border-tint/10 rounded-2xl p-8 text-center">
                    <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-danger/10 flex items-center justify-center">
                        <AlertCircle className="w-8 h-8 text-danger" />
                    </div>
                    <h1 className="text-2xl font-bold text-fg mb-2">Invoice Not Found</h1>
                    <p className="text-fg-3">{error}</p>
                </div>
            </div>
        );
    }

    if (!invoice) return null;

    return (
        <div className="min-h-screen bg-panel flex items-center justify-center p-4 md:p-6">
            <div className="max-w-lg w-full">
                <div className="bg-tint/5 backdrop-blur-xl border border-tint/10 rounded-2xl overflow-hidden">

                    {/* Header with Logo */}
                    <div
                        className="px-8 pt-10 pb-8 text-center"
                        style={{
                            background: `linear-gradient(135deg, ${accentColor}15 0%, transparent 50%)`,
                        }}
                    >
                        {/* Garage Logo */}
                        {logoUrl ? (
                            <div className="mb-5">
                                <img
                                    src={logoUrl}
                                    alt={invoice.garage_name}
                                    data-theme="paper" className="h-14 w-auto mx-auto object-contain bg-card rounded-lg p-1.5"
                                />
                            </div>
                        ) : (
                            <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-tint/10 flex items-center justify-center">
                                <FileText className="w-8 h-8 text-fg" />
                            </div>
                        )}

                        <h1 className="text-2xl md:text-3xl font-bold text-fg mb-1">
                            Invoice #{invoice.invoice_number}
                        </h1>
                        <p className="text-fg-3 text-sm">
                            from {invoice.garage_name}
                        </p>

                        {/* Due Date */}
                        {invoice.due_date && (
                            <div className="flex items-center justify-center gap-1.5 mt-3 text-fg-3 text-xs">
                                <Calendar className="w-3.5 h-3.5" />
                                <span>Due by {formatDate(invoice.due_date)}</span>
                            </div>
                        )}
                    </div>

                    {/* Vehicle Info */}
                    {invoice.vehicle_reg && (
                        <div className="px-8 py-5 border-t border-tint/5">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-tint/5 flex items-center justify-center flex-shrink-0">
                                    <Car className="w-5 h-5 text-fg-3" />
                                </div>
                                <div>
                                    <p className="text-fg font-medium text-sm">
                                        {invoice.vehicle_year && `${invoice.vehicle_year} `}
                                        {invoice.vehicle_make} {invoice.vehicle_model}
                                    </p>
                                    <p className="text-fg-3 text-xs font-mono">{invoice.vehicle_reg}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Line Items */}
                    {allItems.length > 0 && (
                        <div className="border-t border-tint/5">
                            <button
                                onClick={() => setShowItems(!showItems)}
                                className="w-full px-8 py-4 flex items-center justify-between text-sm text-fg-3 hover:text-fg-2 hover:bg-tint/[0.02] transition-all"
                            >
                                <span className="flex items-center gap-2">
                                    <FileText className="w-4 h-4" />
                                    {allItems.length} item{allItems.length !== 1 ? 's' : ''} on this invoice
                                </span>
                                <span className="text-xs" style={{ color: accentColor }}>
                                    {showItems ? 'Hide details ▲' : 'View details ▼'}
                                </span>
                            </button>

                            {showItems && (
                                <div className="px-8 pb-5 space-y-1.5 animate-[fadeIn_0.2s_ease]">
                                    {/* Labour items */}
                                    {labourItems.length > 0 && (
                                        <div className="mb-3">
                                            <div className="flex items-center gap-1.5 mb-1.5">
                                                <Wrench className="w-3.5 h-3.5 text-info" />
                                                <span className="text-xs font-bold text-info uppercase tracking-wider">Labour</span>
                                            </div>
                                            {labourItems.map((item, i) => (
                                                <div key={`l-${i}`} className="flex items-center justify-between py-1.5 text-sm">
                                                    <span className="text-fg-2 truncate mr-4 flex-1">{item.description}</span>
                                                    <span className="text-fg-3 font-mono text-xs tabular-nums whitespace-nowrap">
                                                        £{Number(item.line_total).toFixed(2)}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Part items */}
                                    {partItems.length > 0 && (
                                        <div className="mb-3">
                                            <div className="flex items-center gap-1.5 mb-1.5">
                                                <Package className="w-3.5 h-3.5 text-gold" />
                                                <span className="text-xs font-bold text-gold uppercase tracking-wider">Parts</span>
                                            </div>
                                            {partItems.map((item, i) => (
                                                <div key={`p-${i}`} className="flex items-center justify-between py-1.5 text-sm">
                                                    <div className="flex-1 truncate mr-4">
                                                        <span className="text-fg-2">{item.description}</span>
                                                        {item.quantity > 1 && (
                                                            <span className="text-fg-3 text-xs ml-1.5">×{item.quantity}</span>
                                                        )}
                                                    </div>
                                                    <span className="text-fg-3 font-mono text-xs tabular-nums whitespace-nowrap">
                                                        £{Number(item.line_total).toFixed(2)}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Other items if any */}
                                    {otherItems.length > 0 && (
                                        <div className="mb-3">
                                            {otherItems.map((item, i) => (
                                                <div key={`o-${i}`} className="flex items-center justify-between py-1.5 text-sm">
                                                    <span className="text-fg-2 truncate mr-4 flex-1">{item.description}</span>
                                                    <span className="text-fg-3 font-mono text-xs tabular-nums whitespace-nowrap">
                                                        £{Number(item.line_total).toFixed(2)}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Subtotal & VAT */}
                                    <div className="border-t border-tint/5 pt-3 mt-2 space-y-1.5">
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-fg-3">Subtotal</span>
                                            <span className="text-fg-3 font-mono text-xs tabular-nums">
                                                £{Number(invoice.total_gross - invoice.vat_amount).toFixed(2)}
                                            </span>
                                        </div>
                                        {invoice.vat_amount > 0 && (
                                            <div className="flex items-center justify-between text-sm">
                                                <span className="text-fg-3">VAT</span>
                                                <span className="text-fg-3 font-mono text-xs tabular-nums">
                                                    £{Number(invoice.vat_amount).toFixed(2)}
                                                </span>
                                            </div>
                                        )}
                                        <div className="flex items-center justify-between text-sm font-bold pt-1.5">
                                            <span className="text-fg-2">Total</span>
                                            <span className="text-fg font-mono tabular-nums">
                                                £{Number(invoice.total_gross).toFixed(2)}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Amount Due — with partial payment indicator */}
                    <div className="px-8 py-6 border-t border-tint/5">
                        {hasPaidSomething && (
                            <div className="mb-4 p-3 bg-success/10 border border-success/20 rounded-lg text-sm">
                                <div className="flex items-center justify-between text-success">
                                    <span>Already Paid</span>
                                    <span className="font-mono tabular-nums">£{Number(invoice.amount_paid).toFixed(2)}</span>
                                </div>
                            </div>
                        )}

                        <div className="flex items-center justify-between">
                            <span className="text-fg-3 text-sm">
                                {hasPaidSomething ? 'Remaining Balance' : 'Amount Due'}
                            </span>
                            <span
                                className="text-3xl font-bold font-mono tabular-nums"
                                style={{ color: accentColor }}
                            >
                                £{Number(invoice.balance_due).toFixed(2)}
                            </span>
                        </div>
                        {invoice.vat_amount > 0 && !hasPaidSomething && (
                            <p className="text-right text-xs text-fg-3 mt-1">
                                inc. £{Number(invoice.vat_amount).toFixed(2)} VAT
                            </p>
                        )}
                    </div>

                    {/* Pay Now Button */}
                    <div className="px-8 py-6 border-t border-tint/5">
                        {error && (
                            <div className="mb-4 p-3 bg-danger/10 border border-danger/20 rounded-lg flex items-center gap-2 text-danger text-sm">
                                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                {error}
                            </div>
                        )}

                        <button
                            onClick={handlePayNow}
                            disabled={paying}
                            className="w-full flex items-center justify-center gap-3 px-6 py-4 rounded-xl font-bold text-on-fill text-lg transition-all duration-200 hover:opacity-90 hover:scale-[1.01] active:scale-[0.99] disabled:opacity-50 disabled:hover:scale-100"
                            style={{ backgroundColor: accentColor }}
                        >
                            {paying ? (
                                <Loader2 className="w-6 h-6 animate-spin" />
                            ) : (
                                <CreditCard className="w-6 h-6" />
                            )}
                            {paying ? 'Redirecting to Checkout...' : `Pay £${Number(invoice.balance_due).toFixed(2)} Now`}
                        </button>

                        {/* Download Invoice PDF */}
                        {invoice.pdf_url && (
                            <a
                                href={invoice.pdf_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-full flex items-center justify-center gap-2 px-4 py-3 mt-3 rounded-xl text-sm font-medium text-fg-3 bg-tint/5 border border-tint/10 transition-all duration-200 hover:bg-tint/10 hover:text-fg"
                            >
                                <Download className="w-4 h-4" />
                                Download Invoice PDF
                            </a>
                        )}

                        {/* Trust Badges */}
                        <div className="flex items-center justify-center gap-4 mt-5">
                            <div className="flex items-center gap-1.5 text-fg-3 text-xs">
                                <Lock className="w-3.5 h-3.5" />
                                <span>256-bit SSL</span>
                            </div>
                            <div className="w-px h-3 bg-pressed" />
                            <div className="flex items-center gap-1.5 text-fg-3 text-xs">
                                <Shield className="w-3.5 h-3.5" />
                                <span>Powered by Stripe</span>
                            </div>
                        </div>
                    </div>

                    {/* Garage Contact Info */}
                    {(invoice.garage_phone || invoice.garage_email) && (
                        <div className="px-8 py-5 border-t border-tint/5 bg-tint/[0.02]">
                            <p className="text-fg-3 text-xs uppercase tracking-wider font-bold mb-3">Questions? Contact {invoice.garage_name}</p>
                            <div className="flex flex-wrap gap-4">
                                {invoice.garage_phone && (
                                    <a
                                        href={`tel:${invoice.garage_phone}`}
                                        className="flex items-center gap-2 text-sm text-fg-3 hover:text-fg transition-colors"
                                    >
                                        <Phone className="w-4 h-4" />
                                        <span>{invoice.garage_phone}</span>
                                    </a>
                                )}
                                {invoice.garage_email && (
                                    <a
                                        href={`mailto:${invoice.garage_email}`}
                                        className="flex items-center gap-2 text-sm text-fg-3 hover:text-fg transition-colors"
                                    >
                                        <Mail className="w-4 h-4" />
                                        <span>{invoice.garage_email}</span>
                                    </a>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <p className="text-center text-fg-4 text-xs mt-6">
                    Powered by ForgeDash
                </p>
            </div>

            {/* Animations */}
            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(-8px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </div>
    );
}
